import React, { useEffect, useRef } from 'react';
import { Button, Textarea } from '@roger/ui-components';
import { Send } from 'lucide-react';

export interface SendBarProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  /** Renders above the text area: EMR-1878 pending chips and the limit notice. */
  aboveInput?: React.ReactNode;
  /** Renders at the start of the bottom row, before send: the attach button. */
  toolbarStart?: React.ReactNode;
  /** EMR-1878: a message may carry files and no text. */
  canSendWhenEmpty?: boolean;
}

const MAX_HEIGHT_PX = 140;
/** Only one send bar renders at a time; the empty-channel action focuses it by id. */
export const SEND_INPUT_ID = 'chat-send-input';

export function SendBar({
  title,
  value,
  onChange,
  onSend,
  disabled = false,
  aboveInput,
  toolbarStart,
  canSendWhenEmpty = false,
}: SendBarProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const canSend = (value.trim().length > 0 || canSendWhenEmpty) && !disabled;

  // Grow from one line to about four; the list above is the only scroll area.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  }, [value]);

  return (
    <div className='border-t p-2'>
      {/* index.css removes outlines and rings, so the border is the focus state. */}
      <div className='rounded-lg border focus-within:border-primary'>
        {aboveInput}
        <Textarea
          ref={ref}
          id={SEND_INPUT_ID}
          rows={1}
          value={value}
          placeholder={`Message ${title}`}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'Enter' || e.shiftKey) return;
            e.preventDefault();
            if (canSend) onSend();
          }}
          // min-h-0 drops the variant's 80px floor so the grow effect can rest at one line.
          className='min-h-0 resize-none border-0 bg-transparent'
        />
        {/* items-end: the phone attach row grows the start block upward; send stays on the baseline. */}
        <div className='flex items-end justify-end px-2 pb-2'>
          {toolbarStart && <div className='mr-auto'>{toolbarStart}</div>}
          <Button
            size='icon'
            aria-label='Send'
            disabled={!canSend}
            onClick={onSend}
            className={canSend ? '' : 'bg-primary/40'}
          >
            <Send className='h-4 w-4' />
          </Button>
        </div>
      </div>
      <p className='mt-1 hidden px-1 text-[11px] text-muted-foreground md:block'>
        Enter to send, Shift+Enter for a new line
      </p>
    </div>
  );
}
